import React from 'react';
import { StatsSummary } from '../types';
import { Clock, ShieldCheck, DollarSign, Activity } from 'lucide-react';

interface StatsBarProps {
  stats: StatsSummary | null;
  loading?: boolean;
}

export const StatsBar: React.FC<StatsBarProps> = ({ stats, loading }) => {
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white border border-slate-200 rounded-xl p-4 animate-pulse h-24"
          />
        ))}
      </div>
    );
  }

  const cards = [
    {
      label: 'Active Agents',
      value: stats.activeAgents ?? 5,
      icon: Activity,
      valueColor: 'text-mantis-600',
      iconBg: 'bg-mantis-50 text-mantis-600',
      subtext: 'Key workstreams live',
    },
    {
      label: 'Pending Actions',
      value: stats.pendingCount ?? 12,
      icon: Clock,
      valueColor: 'text-amber-500',
      iconBg: 'bg-amber-50 text-amber-500',
      subtext: 'Requires approval',
    },
    {
      label: 'Monthly Savings',
      value: stats.monthlySavings ?? stats.automationSavingsEstimate ?? '$184K',
      icon: DollarSign,
      valueColor: 'text-mantis-600',
      iconBg: 'bg-mantis-50 text-mantis-600',
      subtext: 'Est. run-rate impact',
    },
    {
      label: 'Compliance Score',
      value: stats.complianceScore ?? '94%',
      icon: ShieldCheck,
      valueColor: 'text-mantis-600',
      iconBg: 'bg-mantis-50 text-mantis-600',
      subtext: 'Portfolio audit-ready',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map((card, idx) => {
        const Icon = card.icon;
        return (
          <div
            key={idx}
            className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center justify-between"
          >
            <div>
              <p className="text-xs font-medium text-slate-500">{card.label}</p>
              <p className={`text-2xl font-bold mt-1 ${card.valueColor}`}>{card.value}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{card.subtext}</p>
            </div>
            <div className={`p-2.5 rounded-lg ${card.iconBg}`}>
              <Icon className="w-5 h-5" />
            </div>
          </div>
        );
      })}
    </div>
  );
};
